import { lazy, Suspense } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import { Layout } from "@/modules/attendance-app/components/Layout";
import { PageLoadingState } from "@/modules/attendance-app/components/LoadingState";

const Attendance = lazy(() =>
	import("@/modules/attendance-app/pages/Attendance").then((m) => ({ default: m.Attendance }))
);
const BuildingDetail = lazy(() =>
	import("@/modules/attendance-app/pages/BuildingDetail").then((m) => ({
		default: m.BuildingDetail,
	}))
);
const Buildings = lazy(() =>
	import("@/modules/attendance-app/pages/Buildings").then((m) => ({ default: m.Buildings }))
);
const Dashboard = lazy(() =>
	import("@/modules/attendance-app/pages/Dashboard").then((m) => ({ default: m.Dashboard }))
);
const DeviceSync = lazy(() =>
	import("@/modules/attendance-app/pages/DeviceSync").then((m) => ({ default: m.DeviceSync }))
);
const Devices = lazy(() =>
	import("@/modules/attendance-app/pages/Devices").then((m) => ({ default: m.Devices }))
);
const Holidays = lazy(() =>
	import("@/modules/attendance-app/pages/Holidays").then((m) => ({ default: m.Holidays }))
);
const Journal = lazy(() =>
	import("@/modules/attendance-app/pages/Journal").then((m) => ({ default: m.Journal }))
);
const Monitoring = lazy(() =>
	import("@/modules/attendance-app/pages/Monitoring").then((m) => ({ default: m.Monitoring }))
);
const PersonList = lazy(() =>
	import("@/modules/attendance-app/pages/PersonList").then((m) => ({ default: m.PersonList }))
);
const PersonProfile = lazy(() =>
	import("@/modules/attendance-app/pages/PersonProfile").then((m) => ({ default: m.PersonProfile }))
);
const Reports = lazy(() =>
	import("@/modules/attendance-app/pages/Reports").then((m) => ({ default: m.Reports }))
);
const SettingsPage = lazy(() =>
	import("@/modules/attendance-app/pages/SettingsPage").then((m) => ({ default: m.SettingsPage }))
);

import { LoginPage } from "@/modules/auth/pages/LoginPage";
import { AuthLayout } from "@/shared/components/layouts/AuthLayout";
import { ProtectedRoute } from "./ProtectedRoute";

export const routes: RouteObject[] = [
	// Public routes - Auth
	{
		path: "/login",
		element: <AuthLayout />,
		children: [
			{
				index: true,
				element: <LoginPage />,
			},
		],
	},

	// Protected routes
	{
		path: "/",
		element: (
			<ProtectedRoute>
				<Layout />
			</ProtectedRoute>
		),
		children: [
			{
				index: true,
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Dashboard />
					</Suspense>
				),
			},
			{
				path: "dashboard",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Dashboard />
					</Suspense>
				),
			},
			{
				path: "monitoring",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Monitoring />
					</Suspense>
				),
			},
			{
				path: "journal",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Journal />
					</Suspense>
				),
			},
			{
				path: "students",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<PersonList type="students" />
					</Suspense>
				),
			},
			{
				path: "students/:id",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<PersonProfile />
					</Suspense>
				),
			},
			{
				path: "teachers",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<PersonList type="teachers" />
					</Suspense>
				),
			},
			{
				path: "teachers/:id",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<PersonProfile />
					</Suspense>
				),
			},
			{
				path: "employees",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<PersonList type="employees" />
					</Suspense>
				),
			},
			{
				path: "employees/:id",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<PersonProfile />
					</Suspense>
				),
			},
			{
				path: "departments",
				element: <Navigate to="/" replace />,
			},
			{
				path: "users/:id",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<PersonProfile />
					</Suspense>
				),
			},
			{
				path: "attendance",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Attendance />
					</Suspense>
				),
			},
			{
				path: "schedules",
				element: <Navigate to="/" replace />,
			},
			{
				path: "buildings",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Buildings />
					</Suspense>
				),
			},
			{
				path: "buildings/:id",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<BuildingDetail />
					</Suspense>
				),
			},
			{
				path: "devices",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Devices />
					</Suspense>
				),
			},
			{
				path: "devices/:id/sync",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<DeviceSync />
					</Suspense>
				),
			},
			{
				path: "reports",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Reports />
					</Suspense>
				),
			},
			{
				path: "settings",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<SettingsPage />
					</Suspense>
				),
			},
			{
				path: "settings/holidays",
				element: (
					<Suspense fallback={<PageLoadingState />}>
						<Holidays />
					</Suspense>
				),
			},
		],
	},

	// 404 - Redirect to dashboard or login
	{
		path: "*",
		element: <Navigate to="/" replace />,
	},
];
